/**
 * useCurrentAccessSubject — resolves the active app user into the minimal
 * {id, systemRole} shape used by the manufacturing-access helpers.
 *
 * Primary source: authService (reads auth_users_config). Fallback: when
 * the user isn't in auth_users_config (e.g. demo personas seeded
 * directly into the users table), query users.system_role directly.
 * This keeps both production auth + ad-hoc demo seeding paths working.
 */
import { useEffect, useState } from 'react'
import { useAppStore } from '@/store/appStore'
import { useAppMode } from '@/hooks/useAppMode'
import { supabase } from '@/lib/supabase'
import { IS_LIVE_SAP } from '@/config/sapBackend'
import * as authService from '@/services/authorizationService'
import type { AccessSubject } from '../lib/manufacturingAccess'
import type { SystemRole } from '@/types/authorization'

/** RFC-4122 UUID shape — used to guard the auth_uid (uuid column) lookup
 * so non-uuid app ids (e.g. demo "demo_user") never 400 PostgREST. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function useCurrentAccessSubject(): {
  subject: AccessSubject | null
  isLoading: boolean
} {
  const currentUser = useAppStore((s) => s.currentUser)
  const roleOverride = useAppStore((s) => s.timesheetRoleOverride)
  // #111 workstream B — demo-only shortcuts (persona role override + the
  // grant-Supervisor fallback below) are gated so a `production` fork resolves
  // the real role from auth/DB and denies by default.
  //
  // #114 — `sandbox` must be treated as a demo-aid mode, NOT a secured fork.
  // On the shared deployment the session is anon in every mode (real auth isn't
  // wired), so switching to Sandbox kept isDemo=false and dropped role
  // resolution into the production deny branch — that's what made the timesheet,
  // personnel, and sandbox project-management pages go empty ("No users row
  // matched ... in production mode"). A real customer fork runs in `production`
  // mode (see supabase/SECURITY_BASELINE.md fork playbook), so the deny-by-
  // default still holds there; only demo + sandbox fail open.
  const { isDemo, isSandbox } = useAppMode()
  // In the S/4-hosted (Option A) build there is no Supabase to resolve a role
  // from, so the SAP-session-derived role (set as timesheetRoleOverride by the
  // whoami identity resolver) is authoritative for NAV/ACTION gating — the DCL
  // in SAP remains the real row-level security boundary. Gated on the cats
  // backend flag so the live (Supabase) app's deny-by-default is unchanged.
  const isSapHostedBuild = IS_LIVE_SAP
  const allowDemoAccess = isDemo || isSandbox || isSapHostedBuild
  const [subject, setSubject] = useState<AccessSubject | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!currentUser?.id) {
      setSubject(null)
      setIsLoading(false)
      return
    }

    // Demo persona switcher override (#1): when set, it's authoritative — the
    // deployed demo is anon and `users` RLS is authenticated-only, so the DB
    // resolution below would otherwise always fall back to 'Supervisor'. Only
    // honored in demo/sandbox; a production fork ignores any stale persisted override.
    if (allowDemoAccess && roleOverride) {
      setSubject({ id: currentUser.id, systemRole: roleOverride as SystemRole })
      setIsLoading(false)
      return
    }

    let cancelled = false

    async function resolve(userId: string): Promise<AccessSubject | null> {
      // Primary: auth_users_config via authService
      try {
        await authService.initializeAuthorization()
        const auth = authService.getAuthUserById(userId)
        if (auth) return { id: auth.id, systemRole: auth.systemRole }
      } catch {
        // fall through
      }

      // Fallback 1: users.id (text) match
      try {
        const { data } = await supabase
          .from('users')
          .select('id, system_role')
          .eq('id', userId)
          .maybeSingle()
        if (data?.system_role) {
          return { id: data.id as string, systemRole: data.system_role as SystemRole }
        }
      } catch {
        // fall through
      }

      // Fallback 2: users.auth_uid match (currentUser.id may be the
      // Supabase auth uuid rather than the app-level text id).
      // Guard: auth_uid is a uuid column — filtering it with a non-uuid
      // text id (e.g. demo personas like "demo_user") makes PostgREST 400
      // (22P02) and spams the console on every role-gated page. A text id
      // can never match a uuid column anyway, so skip the query unless
      // userId is a real UUID.
      if (UUID_RE.test(userId)) {
        try {
          const { data } = await supabase
            .from('users')
            .select('id, system_role')
            .eq('auth_uid', userId)
            .maybeSingle()
          if (data?.system_role) {
            return { id: data.id as string, systemRole: data.system_role as SystemRole }
          }
        } catch {
          // fall through
        }
      }

      // Final fallback. In DEMO/SANDBOX mode, if currentUser is set but no row
      // matches anywhere, grant Supervisor so the demo/sandbox playground can
      // proceed (both are anon on the shared deployment). In a PRODUCTION fork
      // this would be a fail-open (any unresolved authenticated user would get
      // Supervisor), so we deny by default instead — return null, which the
      // access helpers treat as "no role / no access". (#111 workstream B / #114.)
      if (allowDemoAccess) {
        console.warn(
          `[useCurrentAccessSubject] No users row matched id="${userId}" by id, auth_uid, or auth_users_config. ` +
            `Granting Supervisor as a demo/sandbox fallback. (See Phase 2 TS-14 to harden.)`,
        )
        return { id: userId, systemRole: 'Supervisor' as SystemRole }
      }
      console.warn(
        `[useCurrentAccessSubject] No users row matched id="${userId}" in production mode — denying access (no role resolved). ` +
          `Ensure users.system_role / auth_users_config is populated for this account.`,
      )
      return null
    }

    resolve(currentUser.id).then((result) => {
      if (cancelled) return
      setSubject(result)
      setIsLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [currentUser?.id, roleOverride, allowDemoAccess])

  return { subject, isLoading }
}
